import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
    useNicePhoneRequestMutation,
    useNicePhoneVerifyMutation,
    useLazyFindLoginIdQuery,
    useLazyFindPasswordQuery,
    useAuthChangePasswordMutation,
} from "@/redux/authApi";
import { Loading, Toast, Confirm } from "@/components";
import { useFormInput, formatIdNumber, onlyNumericInput, formatTime } from "@/utils";
import { AuthRequestStatus, AuthCodeStatus } from "@/enum";
import { mobileTypeOptions } from "@/constants/selectOptions";

import MehIcon from "@/assets/icons/meh.png";
import EyeIcon from "@/assets/icons/icon-eyes.svg";

export const Auth = () => {
    const navigate = useNavigate();
    const { formData, handleChange, setFormData } = useFormInput({
        // 페이지 상태
        toastMsg: "",
        // 팝업 상태
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        searchType: "ID" /* "ID" or "PASSWORD" */,
        authRequestStatus: AuthRequestStatus.AUTH_NUM_REQUESTED_BEFORE,
        authCodeStatus: AuthRequestStatus.WAITING,
        timer: 0,
        id: null,
        name: "",
        mobileType: "",
        phone: "",
        idNumber: "",
        authNum: "",
        loginId: "",
        phoneAuthenticationId: null,
        password: "",
        passwordCheck: "",
        authView: "VERIFY",
        foundMember: null,
    });
    const {
        toastMsg,
        confirmStatus,
        searchType,
        authRequestStatus,
        authCodeStatus,
        timer,
        id,
        name,
        mobileType,
        phone,
        idNumber,
        authNum,
        phoneAuthenticationId,
        password,
        passwordCheck,
        authView,
        foundMember,
    } = formData;

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            {formData.authView === "VERIFY" && <VerifyPhone formData={formData} handleChange={handleChange} setFormData={setFormData} />}
            {formData.authView === "YOUR_ID_SUCCESS" && <YourIdSuccess formData={formData} />}
            {formData.authView === "NOT_FOUND" && <NotFoundAccount />}
            {formData.authView === "RESET_PASSWORD" && (
                <ResetPassword formData={formData} handleChange={handleChange} setFormData={setFormData} />
            )}
        </>
    );
};

// 아이디/비밀번호 찾기 핸드폰 인증 화면
const VerifyPhone = ({ formData, handleChange, setFormData }) => {
    const [postPhoneVerification] = useNicePhoneRequestMutation();
    const [postVerifyPhone] = useNicePhoneVerifyMutation();
    const [findLoginId] = useLazyFindLoginIdQuery();
    const [findPassword] = useLazyFindPasswordQuery();

    const handleTabClick = (type) => {
        setFormData((prev) => ({
            ...prev,
            searchType: type,
            authRequestStatus: AuthRequestStatus.AUTH_NUM_REQUESTED_BEFORE,
            authCodeStatus: AuthRequestStatus.WAITING,
            timer: 0,
            id: null,
            name: "",
            mobileType: "",
            phone: "",
            idNumber: "",
            authNum: "",
            loginId: "",
            phoneAuthenticationId: null,
            password: "",
            passwordCheck: "",
            authView: "VERIFY",
            foundMember: null,
        }));
    };

    const handleRequestAuth = async () => {
        // 인증 요청이 진행 상태면 타이머 리셋
        if (formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // API 호출: 인증 번호 요청
        try {
            const resp = await postPhoneVerification({
                idNumber: formData.idNumber,
                mobileType: formData.mobileType,
                phone: formData.phone,
                name: formData.name,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                authVerified: false,
                phoneAuthId: resp.data?.id || 0,
                toastMsg: "인증번호가 전송되었습니다.",
            }));

            // 타이머 재시작
            setTimeout(() => {
                setFormData((prev) => ({
                    ...prev,
                    authRequestStatus: AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER,
                    authCodeStatus: AuthCodeStatus.IN_PROGRESS,
                    timer: 180,
                }));
            }, 250);
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "인증번호 요청에 실패했습니다.",
            }));
        }
    };

    const handleSubmit = async () => {
        // 본인인증 (전화번호 인증) API 호출
        try {
            await postVerifyPhone({
                id: formData.phoneAuthId,
                authNum: formData.authNum,
            }).unwrap();

            // 본인 인증 성공 후, 검색 유형에 따라 각각 다른 API 호출
            if (formData.searchType === "ID") {
                const res = await findLoginId({ phone: formData.phone }).unwrap();
                if (res.data) {
                    setFormData((prev) => ({
                        ...prev,
                        authView: "YOUR_ID_SUCCESS",
                        foundMember: res.data,
                        toastMsg: "본인 인증이 완료되었습니다.",
                    }));
                } else {
                    setFormData((prev) => ({
                        ...prev,
                        authView: "NOT_FOUND",
                        toastMsg: "가입된 회원 정보가 존재하지 않습니다.",
                    }));
                }
            } else if (formData.searchType === "PASSWORD") {
                const res = await findPassword({
                    login_id: formData.loginId,
                    phone: formData.phone,
                }).unwrap();
                if (res.data) {
                    setFormData((prev) => ({
                        ...prev,
                        authView: "RESET_PASSWORD",
                        foundMember: res.data, // 해당 member.id를 추후 비밀번호 변경 시 사용
                        toastMsg: "본인 인증이 완료되었습니다.",
                    }));
                } else {
                    setFormData((prev) => ({
                        ...prev,
                        authView: "NOT_FOUND",
                        toastMsg: "해당 회원 정보를 찾을 수 없습니다.",
                    }));
                }
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "본인 인증에 실패했습니다.",
            }));
        }
    };

    // 타이머
    const timerRef = useRef(null);

    const startTimer = useCallback(() => {
        if (formData.authCodeStatus !== AuthCodeStatus.IN_PROGRESS || timerRef.current) return;

        timerRef.current = setInterval(() => {
            setFormData((prev) => {
                const newTimer = prev.timer - 1;
                if (newTimer <= 0) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return { ...prev, authCodeStatus: AuthCodeStatus.TIMEOUT, timer: 0 };
                }
                return { ...prev, timer: newTimer };
            });
        }, 1000);
    }, [formData.authCodeStatus]);

    useEffect(() => {
        if (formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS) {
            startTimer();
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [formData.authCodeStatus, startTimer]);

    useEffect(() => {
        // console.log(formData);
    }, [formData]);

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">아이디/비밀번호 찾기</span>
            </div>

            <div className="page-tab_header">
                <button
                    type="button"
                    className={`btn-tab ${formData.searchType === "ID" ? "active" : ""}`}
                    onClick={() => handleTabClick("ID")}
                >
                    아이디 찾기
                </button>
                <button
                    type="button"
                    className={`btn-tab ${formData.searchType === "PASSWORD" ? "active" : ""}`}
                    onClick={() => handleTabClick("PASSWORD")}
                >
                    비밀번호 찾기
                </button>
            </div>

            <div className="page-content">
                <div className="auth-container">
                    <div className="form-wrapper">
                        {formData.searchType === "PASSWORD" && (
                            <div className="form-item">
                                <div className="item-label">
                                    <span className="label-title">아이디</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="아이디를 입력해주세요."
                                                name="loginId"
                                                value={formData.loginId}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="content-helper">
                                        <span className="helper-text">Helper Text</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">이름</span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="이름을 입력해주세요."
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="content-helper">
                                    <span className="helper-text">Helper Text</span>
                                </div>
                            </div>
                        </div>
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">주민등록번호 앞 7자리</span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="예) 9001011"
                                            name="idNumber"
                                            value={formatIdNumber(formData.idNumber)}
                                            onChange={handleChange}
                                            onInput={onlyNumericInput}
                                            maxLength={8}
                                        />
                                    </div>
                                </div>
                                <div className="content-helper">
                                    <span className="helper-text">Helper Text</span>
                                </div>
                            </div>
                        </div>
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">휴대폰 번호</span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row" style={{ width: "100px", flexShrink: "0" }}>
                                        <Select
                                            options={mobileTypeOptions}
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable={false}
                                            placeholder="선택"
                                            value={mobileTypeOptions.filter((option) => option.value === formData.mobileType)}
                                            onChange={(option) => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    mobileType: option.value,
                                                }));
                                            }}
                                        />
                                    </div>

                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="-없이 휴대폰번호 입력"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            onInput={onlyNumericInput}
                                        />
                                    </div>

                                    <button type="button" className="btn btn-xs btn-primary" onClick={handleRequestAuth}>
                                        {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_BEFORE
                                            ? "인증번호 요청"
                                            : "인증번호 재요청"}
                                    </button>
                                </div>

                                <div className="content-helper">
                                    <span className="helper-text">Helper Text</span>
                                </div>
                            </div>
                            {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER && (
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="인증번호를 입력해주세요."
                                                name="authNum"
                                                value={formData.authNum}
                                                onChange={handleChange}
                                                onInput={onlyNumericInput}
                                            />
                                            <span className="input-esset color-error">
                                                {formData.timer > 0 ? formatTime(formData.timer) : "만료"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="content-helper">
                                        <span className="helper-text">Helper Text</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-button">
                        <div className="button-wrapper">
                            <button
                                className={`btn btn-md button-login ${
                                    formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER
                                        ? "btn-primary"
                                        : "btn-disabled"
                                }`}
                                onClick={handleSubmit}
                            >
                                본인인증하기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const YourIdSuccess = ({ formData }) => {
    const navigate = useNavigate();
    const member = formData.foundMember;

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">
                    <em>{member?.name}</em>님의 아이디입니다.
                </span>
            </div>

            <div className="page-content">
                <div className="auth-container">
                    <div className="success-id">
                        <span className="register-id">{member?.loginId}</span>
                        <span className="register-date">2022.12.12 가입</span>
                    </div>

                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-primary btn-md button-login" onClick={navigate("/login")}>
                                로그인 하러가기
                            </button>
                        </div>
                        {/* 
                        <div className="button-wrapper">
                            <button className="btn btn-transparent btn-md button-login" onClick={navigate("/auth/find")}>
                                비밀번호 찾기
                            </button>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

const NotFoundAccount = () => {
    const navigate = useNavigate();

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">아이디를 찾을 수 없어요!</span>
            </div>

            <div className="page-content">
                <div className="auth-container">
                    <div className="failed-id">
                        <span className="icon">
                            <img src={MehIcon} alt="" />
                        </span>
                        <span className="txt">가입된 회원이 아니예요!</span>
                    </div>

                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-transparent btn-md button-login" onClick={() => navigate("/")}>
                                처음 화면으로 돌아가기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ResetPassword = ({ formData, handleChange, setFormData }) => {
    const [popupConfirmVisible, setPopupConfirmVisible] = useState(false);
    const [authChangePassword] = useAuthChangePasswordMutation();

    const validatePassword = (password, passwordCheck) => {
        if (!password) return "비밀번호를 입력해주세요.";
        if (password !== passwordCheck) return "비밀번호가 일치하지 않습니다.";

        const hasLetter = /[a-zA-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < 8 || !hasLetter || !hasNumber || !hasSpecial) {
            return "비밀번호는 영문, 숫자, 특수문자를 모두 포함한 8자 이상으로 설정해주세요.";
        }
        return "";
    };

    const handleResetPassword = async () => {
        const errMsg = validatePassword(formData.password, formData.passwordCheck);
        if (errMsg) {
            setFormData((prev) => ({ ...prev, toastMsg: errMsg }));
            return;
        }
        try {
            await authChangePassword({ memberId: formData.foundMember.id, password: formData.password }).unwrap();
            setPopupConfirmVisible(true);
        } catch (error) {
            setFormData((prev) => ({ ...prev, toastMsg: error?.message || "비밀번호 변경에 실패했습니다." }));
        }
    };

    return (
        <>
            <div className="app-page">
                <div className="page-header">
                    <span className="header-tit">새로운 비밀번호 입력하기</span>
                </div>

                <div className="page-content">
                    <div className="auth-container">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-label">
                                    <span className="label-title">비밀번호 입력</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="password"
                                                placeholder="변경할 비밀번호를 입력해주세요."
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                            />
                                            <span className="input-esset">
                                                <img src={EyeIcon} alt="" />
                                            </span>
                                        </div>
                                    </div>
                                    <div className="content-helper" style={{ display: "block" }}>
                                        <span className="helper-text">
                                            8자리 이상 이내 영문, 숫자, 특수문자 중 2가지 이상을 조합해주세요 *사용 불가 특수 문자 : ' " + /
                                            ＼ ; : - _ ^ & ( ) &lt; &gt; 제외
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="form-item">
                                <div className="item-label">
                                    <span className="label-title">비밀번호 확인</span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="password"
                                                placeholder="변경할 비밀번호를 재입력해주세요."
                                                name="passwordCheck"
                                                value={formData.passwordCheck}
                                                onChange={handleChange}
                                            />
                                            <span className="input-esset">
                                                <img src={EyeIcon} alt="" />
                                            </span>
                                        </div>
                                    </div>

                                    <div className="content-helper">
                                        <span className="helper-text">Helper Text</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-button">
                            <div className="button-wrapper">
                                <button className="btn btn-primary btn-md button-login" onClick={handleResetPassword}>
                                    비밀번호 재설정
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {popupConfirmVisible && <ResetPasswordConfirmPopup layer={true} onClose={() => setPopupConfirmVisible(false)} />}
        </>
    );
};

const ResetPasswordConfirmPopup = ({ layer = true, onClose }) => {
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup confirm-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>비밀번호가 변경되었어요!</span>
                    </div>
                    <div className="content-description">
                        <p>변경한 비밀번호로 로그인을 진행해보세요.</p>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-close" onClick={() => navigate("/login")}>
                        확인
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
